import { useRef, useEffect, RefObject } from 'react';

interface UseTapNavigationProps {
  onNext: () => void;
  onPrevious: () => void;
  containerRef: RefObject<HTMLElement>; 
  leftAreaPercentage?: number;
  topSafeAreaPercentage?: number;
  bottomSafeAreaPercentage?: number;
  disabled?: boolean;
}

/**
 * A hook that enables tap-based navigation on a container element.
 * Tap on the left side to go to the previous item, tap on the center/right to go to the next item.
 * Avoids top and bottom areas where UI controls are located.
 */
const useTapNavigation = ({ 
  onNext, 
  onPrevious, 
  containerRef, 
  leftAreaPercentage = 40,
  topSafeAreaPercentage = 20,
  bottomSafeAreaPercentage = 20,
  disabled = false 
}: UseTapNavigationProps) => {
  // Store the callback functions in refs to prevent re-renders
  const onNextRef = useRef(onNext);
  const onPreviousRef = useRef(onPrevious);

  // Update refs when callbacks change
  useEffect(() => {
    onNextRef.current = onNext;
    onPreviousRef.current = onPrevious;
  }, [onNext, onPrevious]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || disabled) return;

    const handleTap = (e: MouseEvent | TouchEvent) => {
      // Ignore clicks on play button 
      if ((e.target as HTMLElement).closest('.playButton')) {
        return;
      }
             
      // Get the tap coordinates and container dimensions
      let clientX: number;
      let clientY: number;
      
      if ('touches' in e) {
        // Touch event
        clientX = e.touches[0].clientX;
        clientY = e.touches[0].clientY;
      } else {
        // Mouse event
        clientX = e.clientX;
        clientY = e.clientY;
      }
      
      const containerRect = container.getBoundingClientRect();
      const containerWidth = containerRect.width;
      const containerHeight = containerRect.height;
      
      // Calculate safe area boundaries
      const topSafeAreaHeight = containerHeight * (topSafeAreaPercentage / 100);
      const bottomSafeAreaHeight = containerHeight * (bottomSafeAreaPercentage / 100);
      
      // Check if tap is within the vertical safe area for navigation
      const relativeY = clientY - containerRect.top;
      if (relativeY < topSafeAreaHeight || relativeY > containerHeight - bottomSafeAreaHeight) {
        return; // Tap is in the top or bottom area, ignore
      }
      
      // Calculate the left area boundary
      const leftAreaWidth = containerWidth * (leftAreaPercentage / 100);
      
      // Determine if tap is in left area or right area
      const relativeX = clientX - containerRect.left;
      if (relativeX < leftAreaWidth) {
        // Tap is in the left area
        onPreviousRef.current();
      } else {
        // Tap is in the right area
        onNextRef.current();
      }
    };

    // Add event listeners for both click and touch
    container.addEventListener('click', handleTap);
    
    // Cleanup function
    return () => {
      container.removeEventListener('click', handleTap);
    };
  }, [containerRef, leftAreaPercentage, topSafeAreaPercentage, bottomSafeAreaPercentage, disabled]);
};

export default useTapNavigation;